import React, { useEffect, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Hash, TextIcon, ListFilter } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

// Note: This component uses mock data types and will need to be updated for real data integration
// Mock data interfaces - these should be replaced with real Firebase types
interface ScoutingRun {
  id?: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  locationIds: string[];
  traitIds: string[];
  assigneeIds: string[];
  status: "planned" | "in-progress" | "completed";
}

// Placeholder data - this component needs significant rework to integrate with Firebase
const mockTraits: any[] = [];
const mockTeam: any[] = [];
const locations: any[] = [];
const getFieldById = (id: string) => null;
const getFarmById = (id: string) => null;
const getGrowerById = (id: string) => null;

const scoutingRunSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  description: z.string().optional(),
  startDate: z.date({ required_error: "Start date is required" }),
  endDate: z.date({ required_error: "End date is required" }),
  locationIds: z.array(z.string()).min(1, { message: "At least one location is required" }),
  traitIds: z.array(z.string()).min(1, { message: "At least one trait is required" }),
  assigneeIds: z.array(z.string()).min(1, { message: "At least one assignee is required" }),
  status: z.enum(["planned", "in-progress", "completed"]),
});

interface ScoutingRunDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scoutingRun?: ScoutingRun;
  onSave: (scoutingRun: Omit<ScoutingRun, "id">) => void;
}

const ScoutingRunDialog: React.FC<ScoutingRunDialogProps> = ({ 
  open, 
  onOpenChange, 
  scoutingRun, 
  onSave 
}) => {
  const form = useForm<z.infer<typeof scoutingRunSchema>>({
    resolver: zodResolver(scoutingRunSchema),
    defaultValues: {
      name: "",
      description: "",
      startDate: new Date(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from today
      locationIds: [],
      traitIds: [],
      assigneeIds: [],
      status: "planned",
    },
  });
  
  useEffect(() => {
    if (scoutingRun) {
      form.reset({
        name: scoutingRun.name,
        description: scoutingRun.description,
        startDate: new Date(scoutingRun.startDate),
        endDate: new Date(scoutingRun.endDate),
        locationIds: scoutingRun.locationIds,
        traitIds: scoutingRun.traitIds,
        assigneeIds: scoutingRun.assigneeIds,
        status: scoutingRun.status,
      });
    }
  }, [scoutingRun, form]);
  
  const onSubmit = (values: z.infer<typeof scoutingRunSchema>) => {
    onSave({
      name: values.name,
      description: values.description || "",
      startDate: format(values.startDate, "yyyy-MM-dd"),
      endDate: format(values.endDate, "yyyy-MM-dd"),
      locationIds: values.locationIds,
      traitIds: values.traitIds,
      assigneeIds: values.assigneeIds,
      status: values.status,
    });
    onOpenChange(false);
  };
  
  // Helper function to get trait icon based on type
  const getTraitIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'number':
        return <Hash className="h-4 w-4 mr-2" />;
      case 'category':
        return <ListFilter className="h-4 w-4 mr-2" />;
      case 'text':
        return <TextIcon className="h-4 w-4 mr-2" />;
      default:
        return null;
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{scoutingRun ? "Edit Scouting Run" : "Create Scouting Run"}</DialogTitle>
          <DialogDescription>
            {scoutingRun ? "Update the details of the scouting run" : "Set up a new scouting run for data collection"}
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Scouting run name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Add details about this scouting run" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex flex-col sm:flex-row gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Start Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>End Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="planned">Planned</SelectItem>
                      <SelectItem value="in-progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="locationIds"
              render={() => (
                <FormItem>
                  <FormLabel>Locations</FormLabel>
                  <div className="max-h-48 overflow-y-auto border rounded-md p-2">
                    {locations.map((location) => {
                      const field = getFieldById(location.fieldId);
                      const farm = getFarmById(location.farmId);
                      const grower = getGrowerById(location.growerId);
                      
                      return (
                        <FormField
                          key={location.id}
                          control={form.control}
                          name="locationIds"
                          render={({ field: formField }) => (
                            <FormItem key={location.id} className="flex items-start space-x-2 space-y-0 py-1">
                              <FormControl>
                                <Checkbox
                                  checked={formField.value.includes(location.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      formField.onChange([...formField.value, location.id]);
                                    } else {
                                      formField.onChange(formField.value.filter(val => val !== location.id));
                                    }
                                  }}
                                />
                              </FormControl>
                              <div className="space-y-0 leading-none">
                                <p className="text-sm font-medium">
                                  {field?.name || "Unknown Field"}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {farm?.name || "Unknown Farm"} • {grower?.name || "Unknown Grower"}
                                </p>
                              </div>
                            </FormItem>
                          )}
                        />
                      );
                    })}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="traitIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Traits to collect</FormLabel>
                  <div className="border rounded-md p-4">
                    <div className="grid grid-cols-2 gap-4">
                      {mockTraits.map((trait) => (
                        <div 
                          key={trait.id} 
                          className={cn(
                            "border rounded-md p-3 cursor-pointer relative transition-colors",
                            field.value.includes(trait.id) 
                              ? "border-primary bg-primary/5" 
                              : "hover:bg-accent"
                          )}
                          onClick={() => {
                            if (field.value.includes(trait.id)) {
                              field.onChange(field.value.filter(val => val !== trait.id));
                            } else {
                              field.onChange([...field.value, trait.id]);
                            }
                          }}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            {getTraitIcon(trait.type)}
                            <span className="font-medium">{trait.name}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">{trait.description}</div>
                          
                          {field.value.includes(trait.id) && (
                            <Checkbox 
                              className="absolute top-3 right-3" 
                              checked={true} 
                              onCheckedChange={() => {}} 
                            />
                          )}
                          
                          <div className="mt-2">
                            <Badge variant="outline" className="text-xs bg-background">
                              {trait.type}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="assigneeIds"
              render={() => (
                <FormItem>
                  <FormLabel>Assignees</FormLabel>
                  <div className="max-h-48 overflow-y-auto border rounded-md p-2">
                    {mockTeam.map((member) => (
                      <FormField
                        key={member.id}
                        control={form.control}
                        name="assigneeIds"
                        render={({ field }) => (
                          <FormItem key={member.id} className="flex items-start space-x-2 space-y-0 py-1">
                            <FormControl>
                              <Checkbox
                                checked={field.value.includes(member.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    field.onChange([...field.value, member.id]);
                                  } else {
                                    field.onChange(field.value.filter(val => val !== member.id));
                                  }
                                }}
                              />
                            </FormControl>
                            <div className="space-y-0 leading-none">
                              <p className="text-sm font-medium">{member.firstName} {member.lastName}</p>
                              <p className="text-xs text-gray-500">{member.role}</p>
                            </div>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ScoutingRunDialog;
